import { truncateText } from '@/shared/lib/helpers/truncate'
import { Project, ProjectDto } from '../types'
import { mapGrowthPoint } from './growth-point'

const getDefaultValue = <T>(value: T | undefined, defaultValue: T): T => {
  return value !== undefined ? value : defaultValue
}

export function mapProject(dto: Partial<ProjectDto>): Project {
  return {
    id: getDefaultValue(dto?.id, 0),
    userId: getDefaultValue(dto?.user_id, 0),
    name: getDefaultValue(dto?.name, ''),
    title:
      truncateText({
        text: getDefaultValue(dto?.name, ''),
        maxLength: 50,
      }) || '',
    description: getDefaultValue(dto?.description, ''),
    activity: getDefaultValue(dto?.activity, ''),
    website: getDefaultValue(dto?.website, ''),
    conversions: getDefaultValue(dto?.conversions, ''),
    customerGoalId: getDefaultValue(dto?.customer_goal_id, 0),
    customerRequestId: getDefaultValue(dto?.customer_request_id, 0),
    customerProblemId: getDefaultValue(dto?.customer_problem_id, 0),
    isDefault: getDefaultValue(dto?.is_default, false),
    created: getDefaultValue(dto?.created, ''),
    updated: getDefaultValue(dto?.updated, ''),
    isDeleted: getDefaultValue(dto?.is_deleted, false),
    stateBefore: getDefaultValue(dto?.state_before, ''),
    stateAfter: getDefaultValue(dto?.state_after, ''),
    purpose: getDefaultValue(dto?.purpose, null),
    resources: getDefaultValue(dto?.resources, ''),
    companySizeId: getDefaultValue(dto?.company_size_id, 0),
    billSizeId: getDefaultValue(dto?.bill_size_id, 0),
    productTypeId: getDefaultValue(dto?.product_type_id, 0),
    caseAvailable: getDefaultValue(dto?.case_available, false),
    billSize: {
      id: getDefaultValue(dto?.bill_size?.id, 0),
      name: getDefaultValue(dto?.bill_size?.name, ''),
    },
    companySize: {
      id: getDefaultValue(dto?.company_size?.id, 0),
      name: getDefaultValue(dto?.company_size?.name, ''),
    },
    productType: {
      id: getDefaultValue(dto?.product_type?.id, 0),
      name: getDefaultValue(dto?.product_type?.name, ''),
    },
    customerGoal: {
      id: getDefaultValue(dto?.customer_goal?.id, 0),
      name: getDefaultValue(dto?.customer_goal?.name, ''),
    },
    customerProblem: {
      id: getDefaultValue(dto?.customer_problem?.id, 0),
      name: getDefaultValue(dto?.customer_problem?.name, ''),
    },
    customerRequest: {
      id: getDefaultValue(dto?.customer_request?.id, 0),
      name: getDefaultValue(dto?.customer_request?.name, ''),
    },
    image: getDefaultValue(dto?.image, ''),
    growthPoint: mapGrowthPoint(dto?.growth_point || {}),
    channels:
      dto.channels?.map((channel) => {
        return {
          active: channel?.active || false,
          description: channel?.description || '',
          id: channel?.id || 0,
          image: channel?.image || '',
          name: channel?.name || '',
        }
      }) || [],
  }
}

export function mapProjectToDto(project: Partial<Project>): ProjectDto {
  return {
    id: getDefaultValue(project?.id, 0),
    user_id: getDefaultValue(project?.userId, 0),
    name: getDefaultValue(project?.name, ''),
    description: getDefaultValue(project?.description, ''),
    activity: getDefaultValue(project?.activity, ''),
    website: getDefaultValue(project?.website, ''),
    conversions: getDefaultValue(project?.conversions, ''),
    customer_goal_id: getDefaultValue(project?.customerGoalId, 0),
    customer_request_id: getDefaultValue(project?.customerRequestId, 0),
    customer_problem_id: getDefaultValue(project?.customerProblemId, 0),
    is_default: getDefaultValue(project?.isDefault, false),
    created: getDefaultValue(project?.created, ''),
    updated: getDefaultValue(project?.updated, ''),
    is_deleted: getDefaultValue(project?.isDeleted, false),
    state_before: getDefaultValue(project?.stateBefore, ''),
    state_after: getDefaultValue(project?.stateAfter, ''),
    purpose: getDefaultValue(project?.purpose, null),
    resources: getDefaultValue(project?.resources, ''),
    company_size_id: getDefaultValue(project?.companySizeId, 0),
    bill_size_id: getDefaultValue(project?.billSizeId, 0),
    product_type_id: getDefaultValue(project?.productTypeId, 0),
    case_available: getDefaultValue(project?.caseAvailable, false),
    bill_size: {
      id: getDefaultValue(project?.billSize?.id, 0),
      name: getDefaultValue(project?.billSize?.name, ''),
    },
    company_size: {
      id: getDefaultValue(project?.companySize?.id, 0),
      name: getDefaultValue(project?.companySize?.name, ''),
    },
    product_type: {
      id: getDefaultValue(project?.productType?.id, 0),
      name: getDefaultValue(project?.productType?.name, ''),
    },
    customer_goal: {
      id: getDefaultValue(project?.customerGoal?.id, 0),
      name: getDefaultValue(project?.customerGoal?.name, ''),
    },
    customer_problem: {
      id: getDefaultValue(project?.customerProblem?.id, 0),
      name: getDefaultValue(project?.customerProblem?.name, ''),
    },
    customer_request: {
      id: getDefaultValue(project?.customerRequest?.id, 0),
      name: getDefaultValue(project?.customerRequest?.name, ''),
    },
    image: getDefaultValue(project?.image, ''),
    channels:
      project.channels?.map((channel) => {
        return {
          active: channel?.active || false,
          description: channel?.description || '',
          id: channel?.id || 0,
          image: channel?.image || '',
          name: channel?.name || '',
        }
      }) || [],
  }
}

export function mapProjects(projects: Partial<ProjectDto>[]): Project[] {
  return projects.map((project) => mapProject(project))
}

const mappers = {
  mapProject,
  mapProjectToDto,
  mapProjects,
}

export default mappers
